import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Panel, PanelHeader } from "@/components/ui/Panel";
import { formatMoney } from "@/domain/money";
import { useReceivableForPi } from "@/api/hooks";

/**
 * Tiền đã thu của một PI được trừ cho hóa đơn nào.
 *
 * Khách chốt 14/09/2026: công nợ theo TỪNG HÓA ĐƠN, nhưng tiền vẫn ghi theo PI
 * như đang làm. Bảng này là chỗ kế toán nhìn thấy hai điều đó khớp nhau — không
 * có nó thì việc chia tiền chạy trong bóng tối và không ai đối chiếu được.
 *
 * Chỉ hiện khi PI đã có từ HAI hóa đơn trở lên, hoặc khi có tiền chưa trừ được.
 * Một PI một hóa đơn thì bảng này không nói thêm gì so với ô "đã thu" ngay trên.
 */
export function PiReceivablePanel({ piId, currency }: { piId: string; currency: string }) {
  const { t } = useTranslation();
  const query = useReceivableForPi(piId);
  const data = query.data?.data;
  if (!data) return null;
  if (data.invoices.length < 2 && data.unapplied === 0) return null;

  return (
    <Panel>
      <PanelHeader title={t("pi.receivableTitle")} />
      <ul className="divide-y divide-line">
        {data.invoices.map((row) => (
          <li key={row.invoiceId} className="flex items-center justify-between gap-2 px-4 py-2.5 text-sm">
            <Link className="font-mono text-brand underline" to={`/invoices/${row.invoiceId}`}>
              {row.invNo}
            </Link>
            <span className="text-right">
              {formatMoney(row.paid, currency)}
              <span className="text-ink-faint"> / </span>
              {formatMoney(row.total, currency)}
              {row.outstanding > 0 && (
                <p className="text-xs text-warn">
                  {t("pi.stillOwed")} {formatMoney(row.outstanding, currency)}
                </p>
              )}
            </span>
          </li>
        ))}
      </ul>
      {/*
        Tiền chưa trừ được cho hóa đơn nào phải HIỆN RA. Trên dữ liệu thật
        15/09/2026 có 10 PI đã thu tiền mà chưa xuất hóa đơn nào — tổng hơn
        11.000. Nuốt mất con số đó thì "đã thu" của PI và tổng đã trả của các
        hóa đơn lệch nhau mà không ai giải thích được vì sao.
      */}
      {data.unapplied > 0 && (
        <p className="border-t border-line px-4 py-2.5 text-sm text-warn">
          {t("pi.unapplied")} {formatMoney(data.unapplied, currency)}
        </p>
      )}
    </Panel>
  );
}
